import MacWindowModal from '@components/ui/MacWindowModal';
import { ActionIcon, Button, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { StackSimpleIcon } from '@phosphor-icons/react';
import { useState, type FC } from 'react';

export type GroupingOption = { value: string; label: string };

type Props = {
    /** The dimensions this grid can roll up on, including its "no grouping" entry. */
    options: GroupingOption[];
    /** The value meaning "flat list". Sub-grouping is disabled while this is selected. */
    noneValue: string;
    groupBy: string;
    subGroupBy: string;
    onChange: (groupBy: string, subGroupBy: string) => void;
    /** Copy for the explainer line — each grid aggregates slightly differently. */
    hint?: string;
};

/**
 * The grouping control every DataSheet-backed grid shares: an icon that opens a small
 * window for choosing a level and a level to nest inside it.
 *
 * A single dropdown could only ever express one level, and a second dropdown sitting beside
 * it in the toolbar reads as two independent filters rather than a hierarchy. The window
 * makes the nesting explicit — "group by X, then by Y" — and keeps the toolbar to one
 * affordance however many levels a grid grows.
 *
 * Stateless about the data: it emits the two dimensions and the grid does its own rollup,
 * because aggregation rules differ per grid (quantities sum, ratios are recomputed) and do
 * not belong in a control.
 */
const GroupingLevels: FC<Props> = ({
    options,
    noneValue,
    groupBy,
    subGroupBy,
    onChange,
    hint,
}) => {
    const [opened, setOpened] = useState(false);

    const labelOf = (value: string): string =>
        options.find((option) => option.value === value)?.label ?? 'No grouping';

    // Readable without opening the window, so the current nesting is never hidden.
    const summary =
        groupBy === noneValue
            ? 'No grouping'
            : subGroupBy === noneValue
              ? labelOf(groupBy)
              : `${labelOf(groupBy)} → ${labelOf(subGroupBy)}`;

    return (
        <>
            <Tooltip label={summary}>
                <ActionIcon
                    size="md"
                    variant={groupBy === noneValue ? 'subtle' : 'light'}
                    aria-label="Grouping levels"
                    onClick={() => setOpened(true)}
                >
                    <StackSimpleIcon size={16} />
                </ActionIcon>
            </Tooltip>

            <MacWindowModal
                opened={opened}
                onClose={() => setOpened(false)}
                title="Grouping levels"
                size={520}
            >
                <Stack gap="lg">
                    <Text size="xs" c="dimmed">
                        {hint ??
                            'Rows roll up on the first level, then on the second inside it.'}
                    </Text>

                    <Stack gap={4}>
                        <Text size="13px" fw={600}>
                            Group by
                        </Text>
                        <Select
                            size="xs"
                            value={groupBy}
                            onChange={(value) => {
                                const next = value ?? noneValue;
                                // Nothing to nest inside a flat list, and a sub-level equal
                                // to the parent yields exactly one child per parent.
                                onChange(
                                    next,
                                    next === noneValue || next === subGroupBy
                                        ? noneValue
                                        : subGroupBy,
                                );
                            }}
                            data={options}
                            allowDeselect={false}
                        />
                    </Stack>

                    <Stack gap={4}>
                        <Text size="13px" fw={600}>
                            Then by
                        </Text>
                        <Select
                            size="xs"
                            value={subGroupBy}
                            onChange={(value) => onChange(groupBy, value ?? noneValue)}
                            // The first level is excluded for the reason above.
                            data={options.filter((option) => option.value !== groupBy)}
                            allowDeselect={false}
                            disabled={groupBy === noneValue}
                        />
                        {groupBy === noneValue && (
                            <Text size="xs" c="dimmed">
                                Pick a first level to nest inside.
                            </Text>
                        )}
                    </Stack>

                    <Group justify="flex-end">
                        <Button size="xs" onClick={() => setOpened(false)}>
                            Done
                        </Button>
                    </Group>
                </Stack>
            </MacWindowModal>
        </>
    );
};

export default GroupingLevels;
